import { ReactNode, useEffect, useId, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import InlineConfirm from "../InlineConfirm/InlineConfirm";

type Props = {
  open: boolean;
  title: string;
  children: ReactNode;
  onRestore: () => void;
  // Only called once the user confirms - Discard itself just asks "Delete this draft?" first.
  onDiscard: () => void;
  onClose: () => void;
  // Optional third choice that leaves the draft alone without restoring it (QuoteListPage).
  onNotNow?: () => void;
};

// The "found a local draft - restore it?" prompt shared by QuoteListPage and QuoteDetailPage.
// Issue: Discard sat right next to Restore and deleted the draft immediately, with no undo - now it
// asks to confirm inline first, same idiom as the Drafts dialog's per-row Delete.
const RestoreDraftPrompt = ({ open, title, children, onRestore, onDiscard, onClose, onNotNow }: Props) => {
  const titleId = useId();
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  // Every way of closing (Restore, Not Now, backdrop, Escape, a confirmed Delete) starts the next
  // opening back at the normal choices, not mid-confirm.
  useEffect(() => { if (!open) setConfirmingDiscard(false); }, [open]);

  return <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby={titleId}>
    <DialogTitle id={titleId}>{title}</DialogTitle>
    <DialogContent>{children}</DialogContent>
    <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
      {confirmingDiscard
        ? <InlineConfirm prompt="Delete this draft?" confirmLabel="Delete" onCancel={() => setConfirmingDiscard(false)} onConfirm={onDiscard} />
        : <>
          {onNotNow && <Button color="inherit" onClick={onNotNow}>Not Now</Button>}
          <Button color="inherit" onClick={() => setConfirmingDiscard(true)}>Discard</Button>
          <Button variant="contained" onClick={onRestore}>Restore</Button>
        </>}
    </DialogActions>
  </Dialog>;
};

export default RestoreDraftPrompt;
